// Explicitly import React with named imports for better bundling and to ensure all required features are available

// Import CSS for Vite to process with correct MIME type
import './index.css';

// Import modules at the top level as required
import * as React from 'react';

import { createRoot } from 'react-dom/client';

import { BrowserRouter } from 'react-router-dom';


import ReactDOM from 'react-dom/client';

import App from './App.tsx';
import { ErrorBoundary } from './components/ErrorBoundary';

// Set up React validation

// Pre-initialization validation function to ensure React is properly loaded
const validateReactInitialization = (): boolean => {
  // Validate core React features required by the application
  const requiredFeatures = [
    'createContext',
    'useState',
    'useEffect',
    'useContext',
    'createElement'
  ];

  try {
    // Check if React object exists
    if (typeof React !== 'object') {
      // React object is not properly initialized
      return false;
    }

    // Check if all required React features are available
    for (const feature of requiredFeatures) {
      if (typeof (React as any)[feature] !== 'function') {
        // Required React feature is not available
        return false;
      }
    }

    // StrictMode validation removed - React.StrictMode is handled properly by JSX transformation
    // and doesn't need explicit validation

    return true;
  } catch (error) {
    // Silent error in production
    return false;
  }
};
// Import our service worker registration utility
import serviceWorkerRegistration from './serviceWorkerRegistration';

// Import the global type extensions
import './types/global.d';

// The Window interface is extended in src/types/global.d.ts

/**
 * Initialize the service worker state and register the service worker if needed
 */
async function initializeServiceWorker() {
  // Only run in browser environment
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize service worker state if it doesn't exist
  if (!window.__medulerServiceWorkerState) {
    window.__medulerServiceWorkerState = {
      registrationAttempted: false,
      updateAvailable: false,
      registration: null,
    };
  }

  // Skip service worker in development mode
  if (import.meta.env.DEV) {
    try {
      await serviceWorkerRegistration.unregister();
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
      }
    } catch {
      // Silently ignore cleanup errors in development
    }
    return;
  }
};

/**
 * Register the service worker in production mode
 */
const registerProductionServiceWorker = () => {
  const registrationPromise = serviceWorkerRegistration.register();
  
  if (!registrationPromise) {
    console.error('Failed to start service worker registration');
    return;
  }
  
  registrationPromise
    .then((registration) => {
      if (!registration) {
        console.error('Service worker registration returned null');
        return;
      }
      
      if (import.meta.env.DEV) {
        console.log('Service worker registered in production mode with scope:', registration.scope);
      }
      
      // Set up a listener for service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New update available
            console.log('New service worker update available');
            if (window.__medulerServiceWorkerState) {
              window.__medulerServiceWorkerState.updateAvailable = true;
            }
            
            // Notify the user about the update
            showUpdateNotification();
          }
        });
      });
    })
    .catch(error => {
      console.error('Service worker registration failed:', error);
      
      // Reset the registration attempt flag to allow retries
      if (window.__medulerServiceWorkerState) {
        window.__medulerServiceWorkerState.registrationAttempted = false;
      }
    });
};

/**
 * Show a notification to the user when a service worker update is available
 */
const showUpdateNotification = () => {
  // Create a notification element or use a toast library
  const notification = document.createElement('div');
  notification.style.position = 'fixed';
  notification.style.bottom = '20px';
  notification.style.right = '20px';
  notification.style.padding = '12px 24px';
  notification.style.backgroundColor = '#4CAF50';
  notification.style.color = 'white';
  notification.style.borderRadius = '4px';
  notification.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
  notification.style.zIndex = '1000';
  notification.style.display = 'flex';
  notification.style.alignItems = 'center';
  notification.style.gap = '12px';
  
  const message = document.createElement('span');
  message.textContent = 'A new version is available. ';
  
  const updateButton = document.createElement('button');
  updateButton.textContent = 'Update now';
  updateButton.style.padding = '4px 12px';
  updateButton.style.backgroundColor = 'white';
  updateButton.style.color = '#4CAF50';
  updateButton.style.border = 'none';
  updateButton.style.borderRadius = '4px';
  updateButton.style.cursor = 'pointer';
  updateButton.style.fontWeight = 'bold';
  
  updateButton.addEventListener('click', () => {
    // Post a message to the service worker to skip waiting
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Reload the page to apply the update
    window.location.reload();
  });
  
  notification.appendChild(message);
  notification.appendChild(updateButton);
  document.body.appendChild(notification);
  
  // Auto-dismiss after 10 seconds
  setTimeout(() => {
    notification.style.transition = 'opacity 0.5s';
    notification.style.opacity = '0';
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 500);
  }, 10000);
};

/**
 * Unregister all service workers in development mode
 */
const unregisterDevelopmentServiceWorkers = async () => {
  try {
    const unregistered = await serviceWorkerRegistration.unregister();
    if (import.meta.env.DEV) {
      if (unregistered) {
        console.log('Successfully unregistered service workers for development');
      } else {
        console.log('No service workers were registered to unregister');
      }
    }
  } catch (error) {
    console.error('Error unregistering service workers in development:', error);
  }
};


// Initialize the service worker when the app starts
initializeServiceWorker();

const isReactValid = validateReactInitialization();

// Get root element for mounting
const rootElement = document.getElementById('root');

if (!rootElement) {
  // Root element not found - cannot mount application
} else if (!isReactValid) {
  // Display a user-friendly error when React validation fails
  rootElement.innerHTML = `
    <div style="padding: 20px; font-family: sans-serif;">
      <h2 style="color: #6b46c1;">Meduler</h2>
      <p>We're experiencing technical difficulties loading the application.</p>
      <p>Please try refreshing your browser or clearing your cache.</p>
    </div>
  `;
} else {
  // Initialize the application with proper error handling and performance monitoring
  try {
    // Initialize service worker before mounting the app
    initializeServiceWorker();

    const root = createRoot(rootElement);
    // Remove StrictMode for troubleshooting as it can cause double-rendering
    // and might be contributing to hanging issues
    root.render(
      // Temporarily disable StrictMode for troubleshooting
      // <React.StrictMode>
        <ErrorBoundary>
          <React.Profiler id="MedulerApp" onRender={() => {
            // Performance logging removed for production
          }}>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
              <App />
            </BrowserRouter>
          </React.Profiler>
        </ErrorBoundary>
      // </React.StrictMode>
    );

    // App mounted successfully
  } catch (mountError) {
    // Silent error handling in production

    // Provide a fallback UI in case of mounting errors
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 20px; font-family: sans-serif;">
          <h2 style="color: #6b46c1;">Meduler</h2>
          <p>We encountered an error while starting the application.</p>
          <p>Please try again later or contact support if the issue persists.</p>
        </div>
      `;
    }
  }
}